// A single, permanently-mounted, visually-hidden live region per aria-live level.
// Status text toggled in/out via v-if/v-show mounts its role/aria-live container and
// its text in the same DOM update, which many screen readers don't reliably announce.
// Mutating the text content of a region that already existed before the change does.
//
// announce() clears the region now, then restores the message after RESTORE_DELAY_MS.
// 0ms let a screen reader coalesce the clear and the restore into one no-op, so a
// message announced twice in a row (or announced too close to a prior one) went
// unheard the second time.
//
// A modal dialog (aria-modal="true") hides everything outside itself from assistive
// tech while it's open, so the body-level regions above can go unheard whenever a
// dialog is open. A component that owns its own live regions (see GbDialog.vue)
// registers them here with pushDialogLiveRegions(); announce() prefers the most
// recently registered pair over the body-level fallback for as long as it stays
// registered.
type Level = 'polite' | 'assertive'
type RegionPair = Record<Level, HTMLElement>

const RESTORE_DELAY_MS = 120

// A click-triggered confirmation (e.g. "Statements copied!") fires at the same
// moment the assistive tech is still announcing the button activation itself,
// so the default restore delay isn't enough — give it room to finish first.
export const ACTIVATION_RESTORE_DELAY_MS = 400

const regions: Record<Level, HTMLDivElement | null> = { polite: null, assertive: null }
const restoreTimeoutIds = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>()
const dialogRegionStack: RegionPair[] = []

function activeRegion(level: Level): HTMLElement | null {
  const activeDialog = dialogRegionStack[dialogRegionStack.length - 1]
  return activeDialog ? activeDialog[level] : regions[level]
}

function createRegion(id: string, ariaLive: Level): HTMLDivElement {
  const el = document.createElement('div')
  el.id = id
  el.className = 'sr-only'
  el.setAttribute('role', ariaLive === 'assertive' ? 'alert' : 'status')
  el.setAttribute('aria-live', ariaLive)
  el.setAttribute('aria-atomic', 'true')
  document.body.appendChild(el)
  return el
}

export function mountLiveAnnouncer(): void {
  regions.polite = regions.polite || createRegion('live-announcer-polite', 'polite')
  regions.assertive = regions.assertive || createRegion('live-announcer-assertive', 'assertive')
}

export function pushDialogLiveRegions(pair: RegionPair): () => void {
  dialogRegionStack.push(pair)
  return () => {
    const index = dialogRegionStack.indexOf(pair)
    if (index !== -1) dialogRegionStack.splice(index, 1)
    // Once unregistered, activeRegion() can no longer resolve back to this pair, so a
    // still-pending restore for it would never be reachable by clearAnnouncement() and
    // would write a stale message straight into the dialog's own region later.
    cancelPendingRestore(pair.polite)
    cancelPendingRestore(pair.assertive)
  }
}

function cancelPendingRestore(region: HTMLElement): void {
  clearTimeout(restoreTimeoutIds.get(region))
  restoreTimeoutIds.delete(region)
}

export function announce(
  message: string,
  { assertive = false, delayMs = RESTORE_DELAY_MS }: { assertive?: boolean; delayMs?: number } = {}
): void {
  mountLiveAnnouncer()
  const level: Level = assertive ? 'assertive' : 'polite'
  const region = activeRegion(level)!

  cancelPendingRestore(region)
  region.textContent = ''
  restoreTimeoutIds.set(region, setTimeout(() => {
    region.textContent = message
  }, delayMs))
}

// Cancels a still-pending announce() restore for the given level, so a stale
// message (e.g. "Generating...") can't land after the state it described has
// already changed.
export function clearAnnouncement(level: Level = 'polite'): void {
  const region = activeRegion(level)
  if (!region) return
  cancelPendingRestore(region)
  region.textContent = ''
}

export function unmountLiveAnnouncerForTests(): void {
  dialogRegionStack.length = 0
  regions.polite?.remove()
  regions.assertive?.remove()
  regions.polite = null
  regions.assertive = null
}
